import { openDB, setData, getData, deleteData } from "@/lib/idb-core";

const DB_NAME = "KorimyMessages";
const DB_VERSION = 1;

// 채팅방별 메시지 개수 제한
export const MAX_MESSAGES_PER_CHAT = 500;

const TABLES_BY_VERSION: Record<number, string[]> = {
  1: ["chat-messages", "etc-messages"],
};

const ALL_TABLES = Array.from(new Set(Object.values(TABLES_BY_VERSION).flat()));

// DB 초기화
let dbInstance: IDBDatabase | null = null;

const initializeDB = async (): Promise<IDBDatabase> => {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB(
    DB_NAME,
    DB_VERSION,
    (event: IDBVersionChangeEvent) => {
      const db = (event.target as IDBOpenDBRequest).result;

      for (
        let version = event.oldVersion + 1;
        version <= DB_VERSION;
        version++
      ) {
        const tables = TABLES_BY_VERSION[version] || [];
        tables.forEach((table) => {
          if (!db.objectStoreNames.contains(table)) {
            db.createObjectStore(table, { keyPath: "id" });
          }
        });
      }
    },
  );

  return dbInstance;
};

// 캐시된 메시지 데이터 타입
export interface CachedChatMessages {
  messages: any[];
  hasMore: boolean;
  lastUpdated: number;
}

// 메시지 캐시 키 생성 (userId + chatId)
const getMessagesKey = (userId: string, chatId: string) =>
  `${userId}_${chatId}`;

// 메시지 조회
export const getCachedMessagesFromIDB = async (
  userId: string,
  chatId: string,
): Promise<CachedChatMessages | null> => {
  try {
    if (!userId) {
      console.warn("[IDB-Messages] getCachedMessagesFromIDB: userId is null");
      return null;
    }

    const db = await initializeDB();
    const key = getMessagesKey(userId, chatId);
    const result = await getData<CachedChatMessages>(
      db,
      "chat-messages",
      key,
      ALL_TABLES,
    );

    if (result?.data) {
      console.log(`[IDB-Messages] Loaded messages for chat ${chatId}`);
      return result.data;
    }
    return null;
  } catch (error) {
    console.error("[IDB-Messages] Error getting cached messages:", error);
    return null;
  }
};

// 메시지 저장
export const saveCachedMessagesToIDB = async (
  userId: string,
  chatId: string,
  data: CachedChatMessages,
): Promise<void> => {
  try {
    if (!userId) {
      console.warn("[IDB-Messages] saveCachedMessagesToIDB: userId is null");
      return;
    }

    const db = await initializeDB();
    const key = getMessagesKey(userId, chatId);
    await setData(db, "chat-messages", key, data, ALL_TABLES);
    console.log(`[IDB-Messages] Saved messages for chat ${chatId}`);
  } catch (error) {
    console.error("[IDB-Messages] Error saving cached messages:", error);
  }
};

// 특정 채팅방 메시지 삭제
export const deleteCachedMessagesFromIDB = async (
  userId: string,
  chatId: string,
): Promise<void> => {
  try {
    if (!userId) {
      console.warn(
        "[IDB-Messages] deleteCachedMessagesFromIDB: userId is null",
      );
      return;
    }
    const db = await initializeDB();
    const key = getMessagesKey(userId, chatId);
    await deleteData(db, "chat-messages", key, ALL_TABLES);
    console.log(`[IDB-Messages] Deleted messages for chat ${chatId}`);
  } catch (error) {
    console.error("[IDB-Messages] Error deleting cached messages:", error);
  }
};

// 모든 캐시 삭제 (로그아웃 시)
export const clearAllCachedMessages = async (userId: string): Promise<void> => {
  try {
    const db = await initializeDB();
    const transaction = db.transaction(["chat-messages"], "readwrite");
    const store = transaction.objectStore("chat-messages");
    const request = store.openCursor();
    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest).result;
      if (cursor) {
        const key = cursor.key as string;
        if (key.startsWith(`${userId}_`)) {
          cursor.delete();
        }
        cursor.continue();
      } else {
        console.log("[IDB-Messages] Cleared all cached messages");
      }
    };
    request.onerror = (event) => {
      console.error("[IDB-Messages] Error clearing cached messages:", event);
    };
  } catch (error) {
    console.error("[IDB-Messages] Error clearing cached messages:", error);
  }
};

// 오래된 메시지 정리
export const trimCachedMessages = async (
  userId: string,
  chatId: string,
  maxMessages: number = MAX_MESSAGES_PER_CHAT,
): Promise<void> => {
  const cached = await getCachedMessagesFromIDB(userId, chatId);
  if (cached && cached.messages.length > maxMessages) {
    const trimmed = {
      ...cached,
      messages: cached.messages.slice(0, maxMessages),
      hasMore: true,
    };
    await saveCachedMessagesToIDB(userId, chatId, trimmed);
  }
};

// 전체 캐시 크기 확인
export const getCacheSizeStats = async (
  userId: string,
): Promise<{
  totalChats: number;
  totalMessages: number;
  estimatedSize: number;
}> => {
  const db = await initializeDB();
  const transaction = db.transaction(["chat-messages"], "readonly");
  const store = transaction.objectStore("chat-messages");
  const request = store.openCursor();

  return new Promise((resolve) => {
    let totalChats = 0;
    let totalMessages = 0;

    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest).result;
      if (cursor) {
        const key = cursor.key as string;
        if (key.startsWith(`${userId}_`)) {
          totalChats++;
          totalMessages += cursor.value.data?.messages?.length || 0;
        }
        cursor.continue();
      } else {
        resolve({
          totalChats,
          totalMessages,
          estimatedSize: totalMessages * 500, // 메시지당 평균 500바이트 추정
        });
      }
    };
  });
};
